import { supabase } from '@/integrations/supabase/client';

export interface InvestorIdentity {
  id: string;
  user_id: string;
  shareholder_id: string;
  created_at: string;
}

export interface CreateInvestorIdentityParams {
  email: string;
  shareholderId: string;
}

/**
 * Get user ID by email using RPC function
 */
export async function getUserByEmail(email: string): Promise<{ id: string; email: string } | null> {
  const { data, error } = await supabase.rpc('get_user_by_email', { p_email: email.trim() });

  if (error) throw error;

  if (!data || data.length === 0) {
    return null;
  }

  return data[0];
}

/**
 * Create investor identity mapping
 */
export async function createInvestorIdentity(params: CreateInvestorIdentityParams): Promise<void> {
  const user = await getUserByEmail(params.email);

  if (!user) {
    throw new Error(`No user found with email: ${params.email}`);
  }

  const { error } = await supabase.from('investor_identities').insert({
    user_id: user.id,
    shareholder_id: params.shareholderId,
  });

  if (error) {
    if (error.code === '23505') {
      throw new Error('This investor identity mapping already exists');
    }
    throw error;
  }
}

/**
 * Get all investor identities
 */
export async function getInvestorIdentities(): Promise<InvestorIdentity[]> {
  const { data, error } = await supabase
    .from('investor_identities')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Get investor identities for a specific shareholder
 */
export async function getInvestorIdentitiesByShareholder(
  shareholderId: string
): Promise<InvestorIdentity[]> {
  const { data, error } = await supabase
    .from('investor_identities')
    .select('*')
    .eq('shareholder_id', shareholderId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Delete investor identity mapping
 */
export async function deleteInvestorIdentity(mappingId: string): Promise<void> {
  const { error } = await supabase.from('investor_identities').delete().eq('id', mappingId);

  if (error) throw error;
}

/**
 * Check if a user has investor identity linked
 */
export async function hasInvestorIdentity(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('investor_identities')
    .select('id')
    .eq('user_id', userId)
    .limit(1);

  if (error) throw error;
  return (data?.length || 0) > 0;
}
